import { CircularProgress, ComboBox } from '@civilgrid/ui/components';
import { useNavigate } from '@tanstack/react-router';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type Row,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useAsyncRetry } from 'react-use';
import { getUnapprovedUsers as apiGetUnapprovedUsers } from '~/api/internal';
import type { User } from '~/common/types';
import { useIsKeyDown } from '~/hooks/useIsKeyDown';
import CivilSearchTable from '../civilsearch/common/CivilSearchTable';
import { ALL_ORGANIZATIONS, buildOrganizationOptions } from './columnHelpers';
import { columns } from './columns';
import UserDetails from './UserDetails';

export default function UnapprovedUsers() {
  const navigate = useNavigate();
  const query = useAsyncRetry(() => apiGetUnapprovedUsers(), []);
  const [isMetaKeyDown] = useIsKeyDown('Meta');
  const [organization, setOrganization] = useState<string>(ALL_ORGANIZATIONS);

  const users = query.value ?? [];

  const organizationOptions = useMemo(() => buildOrganizationOptions(users), [users]);

  const columnFilters = useMemo(() => [{ id: 'organization', value: organization }], [organization]);

  const table = useReactTable({
    data: users,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    enableSorting: true,
    state: { columnFilters },
    initialState: {
      sorting: [
        { id: 'completedPersona', desc: false },
        { id: 'email', desc: false },
      ],
      pagination: { pageIndex: 0, pageSize: 100 },
    },
  });

  const onRowClick = (row: Row<User>) => {
    const id = row.original.id;
    const location = `/admin/unapproved-users?userId=${id}`;

    if (isMetaKeyDown) {
      const url = `${window.location.origin}${location}`;
      window.open(url, '_blank');
    } else {
      navigate({ to: location });
    }
  };

  if (query.loading) return <CircularProgress indeterminate />;
  if (query.error || !query.value) {
    return <div>An error occurred fetching unapproved users.</div>;
  }

  return (
    <div className="relative flex h-full w-full grow overflow-clip">
      <div className="w-2/3 grow overflow-auto px-2">
        <CivilSearchTable id="unapproved-users-table" table={table} onRowClick={onRowClick}>
          <ComboBox
            options={organizationOptions}
            value={organization}
            onValueChange={setOrganization}
            placeholder="Filter by organization"
            searchPlaceholder="Search organizations..."
            emptyText="No organizations found."
            triggerClassName="w-[260px]"
            data-testid="unapproved-users-org-filter"
          />
        </CivilSearchTable>
      </div>
      <div className="w-1/3 overflow-auto border-l px-2">
        <UserDetails users={query.value} refetch={query.retry} />
      </div>
    </div>
  );
}
